// src/features/clanEvents/useClanEvents.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

export type ClanEventType = 'xp' | 'boss' | 'combined';
export type ClanEventStatus = 'scheduled' | 'active' | 'ending' | 'ended' | 'cancelled';

export interface PrizeEntry {
  place: number;
  prize: string;
}

export interface ClanEvent {
  id: number;
  channel_id: string;
  clan_name: string;
  type: ClanEventType;
  status: ClanEventStatus;
  starts_at: string;
  ends_at: string;
  duration_days: number;
  skills: string[] | null;
  categories: string[] | null;
  prize_pool: PrizeEntry[] | null;
  raffle_enabled: boolean;
  raffle_xp_per_ticket: number | null;
  raffle_kills_per_ticket: number | null;
  raffle_winner_count: number | null;
  raffle_allow_repeat: boolean;
}

// Fields safe to edit once an event is active/ending - mirrors the
// backend's SAFE_WHEN_ACTIVE_FIELDS exactly, so the UI can lock the
// same fields the server would reject anyway (the server remains the
// real enforcement either way).
export const SAFE_WHEN_ACTIVE_FIELDS = new Set([
  'prize_pool', 'channel_id',
  'raffle_enabled', 'raffle_xp_per_ticket', 'raffle_kills_per_ticket',
  'raffle_winner_count', 'raffle_allow_repeat',
]);

export interface ClanEventEditPayload {
  [key: string]: unknown;
  announceChange?: boolean;
}

export interface EventSnapshot {
  status: ClanEventStatus;
  type: ClanEventType;
  xp_snapshot_start: Record<string, Record<string, number>> | null;
  xp_snapshot_end: Record<string, Record<string, number>> | null;
  xp_late_joiners: Record<string, Record<string, number>> | null;
  boss_snapshot_start: Record<string, Record<string, number>> | null;
  boss_snapshot_end: Record<string, Record<string, number>> | null;
  boss_late_joiners: Record<string, Record<string, number>> | null;
}

export function useEventSnapshot(guildId: string | null, eventId: number | null) {
  return useQuery({
    queryKey: ['clan-event-snapshot', guildId, eventId],
    queryFn: () => apiFetch<EventSnapshot>(`/api/clan-events/${guildId}/${eventId}/snapshot`),
    enabled: !!guildId && eventId !== null,
  });
}

export function useClanEvents(guildId: string | null) {
  return useQuery({
    queryKey: ['clan-events', guildId],
    queryFn: () => apiFetch<ClanEvent[]>(`/api/clan-events/${guildId}`),
    enabled: !!guildId,
  });
}

export function useUpdateClanEvent(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, payload }: { eventId: number; payload: ClanEventEditPayload }) =>
      apiFetch(`/api/clan-events/${guildId}/${eventId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clan-events', guildId] }),
  });
}

export function useCancelClanEvent(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: number) => apiFetch(`/api/clan-events/${guildId}/${eventId}/cancel`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clan-events', guildId] }),
  });
}
